import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  GridList,
  GridListTile,
  Link,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { Can } from '../../../components/Can';
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay';
import { Page } from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import { WhereConstraints } from '../../../graphql/common.datatypes';
import { Resource, ResourceOption } from '../../../graphql/resources.datatypes';
import anyLoading from '../../../utils/anyLoading';
import getResources from '../getResources.api';
import ResourceEdit from '../ResourceEdit';
import { getBlankResource } from '../ResourceEdit/ResourceEdit';
import ResourceOptionEdit from '../ResourceOptionEdit';
import ResourcesTree from '../ResourcesTree/ResourcesTree';
import ResourcesSearchFilter, {
  FilterValues,
  getResourcesWhereConstraints,
  initialFilters
} from './ResourcesSearchFilter/ResourcesSearchFilter';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    },
  },
  breadcrumbs: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    height: '70vh',
  },
  gridList: {
    display: 'flex',
    flex: 1,
    height: '70vh',
    flexWrap: 'nowrap',
    width: '100%',
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: 'translateZ(0)',
    scrollBehavior: 'smooth',
  },
  gridTile: {
    minWidth: 320,
    minHeight: '70vh',
  },
  tilewrapper: {
    overflowX: 'scroll',
  },
  gridTileCard: {
    minWidth: 320,
    height: '68vh',
    overflowY: 'scroll',
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
}));

const ResourcesList = () => {
  const classes = useStyles();
  const theme = useTheme();
  const scrollContainer = useRef<HTMLUListElement>();
  const screenSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const screenLarge = useMediaQuery(theme.breakpoints.down('lg'));
  const columns = screenSmall? 1 : screenLarge? 2 : 3;
  const title = 'Resources List';
  const [gridTiles, setGridTiles] = useState<Array<{resource?: Resource, resourceOption?: ResourceOption}>>([]);

  useEffect(() => {
    const element = scrollContainer.current;
    if(element) element.scrollLeft = element.scrollWidth;
  },[gridTiles]);

  const breadcrumbClick = (i: number) => {
    const newGridTiles = [...gridTiles];
    newGridTiles.length = i;
    setGridTiles(newGridTiles)
  };

  const [filters, setFilters] = useState<FilterValues>({...initialFilters()});
  const [whereResources, setWhereResources] = useState<WhereConstraints>(getResourcesWhereConstraints(filters));
  const [treeData, setTreeData] = useState<Resource[]>([]);
  const resourcesData = getResources(whereResources);
  const loadingData = anyLoading(resourcesData);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  useEffect(() => {
    let newData: Resource[] = [];

    if(resourcesData.data && resourcesData.data.allResources) {
      newData = [...newData, ...resourcesData.data.allResources];
    }

    setTreeData(newData.filter((item, pos) => newData.indexOf(item) === pos));

  }, [resourcesData.data]);

  const handleSearch = (value: string) => {
    applyFilters({...initialFilters(), search: value});
  };

  const applyFilters = (newFilters: FilterValues) => {
    setFilters(newFilters);
    setWhereResources(getResourcesWhereConstraints(newFilters));
    setGridTiles([]);
  };

  const getTitleFromGridTileObj = (obj: { resource?: Resource | { internal_name: string }, resourceOption?: ResourceOption }) => {
    let cardTitle = '';
    if(obj.resource) cardTitle = obj.resource.internal_name? obj.resource.internal_name : 'New Resource';
    if(obj.resourceOption) cardTitle = obj.resourceOption!.internal_name? obj.resourceOption!.internal_name : 'New Resource Option';
    return cardTitle;
  };

  return (
    <Page className={classes.root} title={title}>
      <PageHeader section="Resources" title={title} />

      <SearchBar onSearch={handleSearch} filterContent={
        <ResourcesSearchFilter filters={filters} applyFilters={applyFilters}/>
      }/>

      <div className={classes.breadcrumbs}>
        <Breadcrumbs >
          {[{resource: {internal_name: 'Resources Root'}, resourceOption: undefined}, ...gridTiles].map((el, i) => {
            const cardTitle = getTitleFromGridTileObj(el);
            return (<Link key={'breadcrumb_'+i} color="inherit" underline="always" onClick={() => breadcrumbClick(i)}>{cardTitle} ></Link>);
          })}
        </Breadcrumbs>
      </div>

      <div className={classes.container}>
        <GridList
          ref={scrollContainer}
          className={classes.gridList}
          cols={columns}
          spacing={theme.spacing(2)}
        >
          <GridListTile key={1} classes={{tile: classes.tilewrapper,}} className={classes.gridTile}>
            <Card className={classes.gridTileCard}>
              <CardHeader title="Resources Root" action={(
                <Can role={['super-admin']}>
                  <Button
                    className={classes.saveButton}
                    variant="contained"
                    onClick={() => {
                      setGridTiles([{resource: getBlankResource()}])
                    }}
                  >
                    + Resource
                  </Button>
                </Can>
              )} />
              <CardContent>
                <LoadingOverlay loading={loadingData.loading}>
                  <ResourcesTree
                    resources={treeData}
                    onResourceView={(resource: Resource) => {
                      setGridTiles([{resource}])
                    }}
                    onResourceOptionClick={(resourceOption: ResourceOption) => {
                      setGridTiles([{resourceOption}])
                    }}
                    hideViewIds={deletedIds}
                  />
                </LoadingOverlay>
              </CardContent>
            </Card>

          </GridListTile>

          {gridTiles.map((el, i) => {
            const cardTitle =  getTitleFromGridTileObj(el);
            return (
              <GridListTile key={'child_'+i} className={classes.gridTile}>
                <Card className={classes.gridTileCard}>
                  <CardHeader title={cardTitle} />
                  <CardContent>
                    {el.resource && (
                      <ResourceEdit
                        resourceId={el.resource.id}
                        parentId={el.resource.parent_resource_id}
                        onResourceView={(resource: Resource) => {
                          const newGridTiles = [...gridTiles];
                          newGridTiles.length = i+1;
                          setGridTiles([...newGridTiles,{resource}])
                        }}
                        onResourceOptionView={(resourceOption: ResourceOption) => {
                          const newGridTiles = [...gridTiles];
                          newGridTiles.length = i+1;
                          setGridTiles([...newGridTiles,{resourceOption}])
                        }}
                        onResourceCreated={(resource: Resource) => {
                          const newGridTiles = [...gridTiles];
                          newGridTiles[i] = {resource};
                          setGridTiles(newGridTiles);
                        }}
                        onResourceDeleted={(id: number) => {
                          setGridTiles(gridTiles.filter((item) => (item.resource && item.resource.id !== id)))
                          setDeletedIds([...deletedIds, id]);
                        }}
                      />
                      )}

                    {el.resourceOption && (<ResourceOptionEdit
                      resourceOptionId={el.resourceOption!.id!}
                      parentId={el.resourceOption!.resource_id!}
                      onResourceOptionDeleted={(id: number) => {
                        setGridTiles(gridTiles.filter((item) => item.resource || (item.resourceOption && item.resourceOption.id !== id)))
                      }}
                      onResourceOptionCreated={(resourceOption: ResourceOption) => {
                        const newGridTiles = [...gridTiles];
                        newGridTiles[i] = {resourceOption};
                        setGridTiles(newGridTiles);
                      }}
                    />)}
                  </CardContent>
                </Card>
              </GridListTile>
            )
          })}
        </GridList>
      </div>
    </Page>
  );
};

export default ResourcesList;
